import { useEffect, useSyncExternalStore } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  type AccentPalettePreference,
  DEFAULT_ACCENT_PALETTE_PREFERENCE,
  DEFAULT_DASHBOARD_PICKER_PREFERENCES,
  DEFAULT_DISPLAY_SIZE_PREFERENCE,
  DEFAULT_RISK_ASSESSMENT_DETAIL_VIEW_PREFERENCES,
  DEFAULT_THEME_PREFERENCE,
  DEFAULT_WORK_QUEUE_VIEW_PREFERENCES,
  type DashboardPickerPreferences,
  type DisplaySizePreference,
  type PersistedTableColumnOrderModel,
  type PersistedTableColumnVisibilityModel,
  type RiskAssessmentDetailViewPreferences,
  type TableColumnOrderPreferences,
  type TableColumnVisibilityPreferences,
  type ThemePreference,
  USER_PREFERENCES_STORAGE_KEY,
  type WorkQueueViewPreferences,
  getAccentPalettePreferenceFromPersistedStorageValue,
  getDisplaySizePreferenceFromPersistedStorageValue,
  getRiskAssessmentDetailViewPreferencesFromPersistedStorageValue,
  getTableColumnOrderPreferencesFromPersistedStorageValue,
  getTableColumnVisibilityPreferencesFromPersistedStorageValue,
  getThemePreferenceFromPersistedStorageValue,
  normalizeRiskAssessmentDetailViewPreferences,
} from './user-preferences';

import {
  type TableColumnWidthPreferences,
  areTableColumnWidthPreferencesEqual,
  getTableColumnLegacyScopeClaimsFromPersistedStorageValue,
  getTableColumnWidthPreferencesFromPersistedStorageValue,
  normalizeTableColumnWidths,
} from './table-column-width-preferences';

type UserPreferencesState = {
  themePreference: ThemePreference;
  accentPalettePreference: AccentPalettePreference;
  displaySizePreference: DisplaySizePreference;
  riskAssessmentDetailViewPreferences: RiskAssessmentDetailViewPreferences;
  tableColumnVisibilityPreferences: TableColumnVisibilityPreferences;
  tableColumnOrderPreferences: TableColumnOrderPreferences;
  tableColumnWidthPreferences: TableColumnWidthPreferences;
  tableColumnLegacyScopeClaims: Record<string, string>;
  workQueueViewPreferences: WorkQueueViewPreferences;
  dashboardPickerPreferences: DashboardPickerPreferences;
};

type UserPreferencesActions = {
  setThemePreference: (themePreference: ThemePreference) => void;
  setAccentPalettePreference: (
    accentPalettePreference: AccentPalettePreference
  ) => void;
  setDisplaySizePreference: (
    displaySizePreference: DisplaySizePreference
  ) => void;
  setRiskAssessmentDetailViewPreferences: (
    preferences: Partial<RiskAssessmentDetailViewPreferences>
  ) => void;
  setTableColumnVisibilityPreference: (
    tableKey: string,
    tableColumnVisibilityModel: PersistedTableColumnVisibilityModel
  ) => void;
  setTableColumnOrderPreference: (
    tableKey: string,
    tableColumnOrderModel: PersistedTableColumnOrderModel
  ) => void;
  setTableColumnWidthPreference: (
    tableKey: string,
    widths: Record<string, number>
  ) => void;
  claimTableColumnLegacyScope: (tableKey: string, scope: string) => void;
  setLastVisitedWorkQueueId: (queueId: string | undefined) => void;
  setWorkQueueFavouritesHintDismissed: (dismissed: boolean) => void;
  setDashboardPickerFamily: (family: number | undefined) => void;
};

function arePersistedTableColumnVisibilityModelsEqual(
  currentModel: PersistedTableColumnVisibilityModel,
  nextModel: PersistedTableColumnVisibilityModel
) {
  const currentKeys = Object.keys(currentModel);
  const nextKeys = Object.keys(nextModel);

  if (currentKeys.length !== nextKeys.length) {
    return false;
  }

  return currentKeys.every((key) => currentModel[key] === nextModel[key]);
}

function areTableColumnVisibilityPreferencesEqual(
  currentPreferences: TableColumnVisibilityPreferences,
  nextPreferences: TableColumnVisibilityPreferences
) {
  const currentKeys = Object.keys(currentPreferences);
  const nextKeys = Object.keys(nextPreferences);

  if (currentKeys.length !== nextKeys.length) {
    return false;
  }

  return currentKeys.every((tableKey) =>
    arePersistedTableColumnVisibilityModelsEqual(
      currentPreferences[tableKey] ?? {},
      nextPreferences[tableKey] ?? {}
    )
  );
}

function arePersistedTableColumnOrderModelsEqual(
  currentModel: PersistedTableColumnOrderModel,
  nextModel: PersistedTableColumnOrderModel
) {
  if (currentModel.length !== nextModel.length) {
    return false;
  }

  return currentModel.every((field, index) => field === nextModel[index]);
}

function areTableColumnOrderPreferencesEqual(
  currentPreferences: TableColumnOrderPreferences,
  nextPreferences: TableColumnOrderPreferences
) {
  const currentKeys = Object.keys(currentPreferences);
  const nextKeys = Object.keys(nextPreferences);

  if (currentKeys.length !== nextKeys.length) {
    return false;
  }

  return currentKeys.every((tableKey) =>
    arePersistedTableColumnOrderModelsEqual(
      currentPreferences[tableKey] ?? [],
      nextPreferences[tableKey] ?? []
    )
  );
}

function areRiskAssessmentDetailViewPreferencesEqual(
  currentPreferences: RiskAssessmentDetailViewPreferences,
  nextPreferences: RiskAssessmentDetailViewPreferences
) {
  return (
    currentPreferences.agentRailIdleExpanded ===
      nextPreferences.agentRailIdleExpanded &&
    currentPreferences.agentTrajectoryWidthPx ===
      nextPreferences.agentTrajectoryWidthPx
  );
}

const initialState = {
  themePreference: DEFAULT_THEME_PREFERENCE,
  accentPalettePreference: DEFAULT_ACCENT_PALETTE_PREFERENCE,
  displaySizePreference: DEFAULT_DISPLAY_SIZE_PREFERENCE,
  riskAssessmentDetailViewPreferences:
    DEFAULT_RISK_ASSESSMENT_DETAIL_VIEW_PREFERENCES,
  tableColumnVisibilityPreferences: {},
  tableColumnOrderPreferences: {},
  tableColumnWidthPreferences: {},
  tableColumnLegacyScopeClaims: {},
  workQueueViewPreferences: DEFAULT_WORK_QUEUE_VIEW_PREFERENCES,
  dashboardPickerPreferences: DEFAULT_DASHBOARD_PICKER_PREFERENCES,
} satisfies UserPreferencesState;

export const useUserPreferencesStore = create<
  UserPreferencesState & UserPreferencesActions
>()(
  persist(
    (set) => ({
      ...initialState,
      setThemePreference: (themePreference) => {
        set((state) =>
          state.themePreference === themePreference
            ? state
            : { ...state, themePreference }
        );
      },
      setAccentPalettePreference: (accentPalettePreference) => {
        set((state) =>
          state.accentPalettePreference === accentPalettePreference
            ? state
            : { ...state, accentPalettePreference }
        );
      },
      setDisplaySizePreference: (displaySizePreference) => {
        set((state) =>
          state.displaySizePreference === displaySizePreference
            ? state
            : { ...state, displaySizePreference }
        );
      },
      setRiskAssessmentDetailViewPreferences: (preferences) => {
        set((state) => {
          const nextPreferences = normalizeRiskAssessmentDetailViewPreferences({
            ...state.riskAssessmentDetailViewPreferences,
            ...preferences,
          });

          if (
            areRiskAssessmentDetailViewPreferencesEqual(
              state.riskAssessmentDetailViewPreferences,
              nextPreferences
            )
          ) {
            return state;
          }

          return {
            ...state,
            riskAssessmentDetailViewPreferences: nextPreferences,
          };
        });
      },
      setTableColumnVisibilityPreference: (
        tableKey,
        tableColumnVisibilityModel
      ) => {
        set((state) => {
          const currentTableColumnVisibilityModel =
            state.tableColumnVisibilityPreferences[tableKey] ?? {};

          if (
            arePersistedTableColumnVisibilityModelsEqual(
              currentTableColumnVisibilityModel,
              tableColumnVisibilityModel
            )
          ) {
            return state;
          }

          return {
            ...state,
            tableColumnVisibilityPreferences: {
              ...state.tableColumnVisibilityPreferences,
              [tableKey]: tableColumnVisibilityModel,
            },
          };
        });
      },
      claimTableColumnLegacyScope: (tableKey, scope) => {
        set((state) =>
          state.tableColumnLegacyScopeClaims?.[tableKey]
            ? state
            : {
                ...state,
                tableColumnLegacyScopeClaims: {
                  ...state.tableColumnLegacyScopeClaims,
                  [tableKey]: scope,
                },
              }
        );
      },
      setTableColumnWidthPreference: (tableKey, widths) => {
        const normalized = normalizeTableColumnWidths(widths);
        set((state) => {
          const current = state.tableColumnWidthPreferences?.[tableKey] ?? {};
          if (
            Object.keys(current).length === Object.keys(normalized).length &&
            Object.entries(normalized).every(
              ([key, width]) => current[key] === width
            )
          )
            return state;
          return {
            ...state,
            tableColumnWidthPreferences: {
              ...state.tableColumnWidthPreferences,
              [tableKey]: normalized,
            },
          };
        });
      },
      setTableColumnOrderPreference: (tableKey, tableColumnOrderModel) => {
        set((state) => {
          const currentTableColumnOrderModel =
            state.tableColumnOrderPreferences[tableKey] ?? [];

          if (
            arePersistedTableColumnOrderModelsEqual(
              currentTableColumnOrderModel,
              tableColumnOrderModel
            )
          ) {
            return state;
          }

          return {
            ...state,
            tableColumnOrderPreferences: {
              ...state.tableColumnOrderPreferences,
              [tableKey]: tableColumnOrderModel,
            },
          };
        });
      },
      /**
       * Written FROM the URL when a work queue is opened, and read only to seed
       * the first navigation into the feature. Passing `undefined` forgets the
       * marker, which is what the queue list does once it has established that
       * the remembered queue no longer exists or is no longer visible.
       */
      setLastVisitedWorkQueueId: (queueId) => {
        set((state) => {
          const nextQueueId = queueId?.trim() ? queueId : undefined;

          if (
            state.workQueueViewPreferences.lastVisitedQueueId === nextQueueId
          ) {
            return state;
          }

          /*
           * Spread rather than replace: this slice holds two independent facts
           * and writing a fresh object would forget the hint dismissal every
           * time the analyst opened a queue.
           *
           * Forgetting the marker OMITS the key rather than storing an
           * explicit `undefined`, so the persisted blob stays the shape a
           * fresh browser would produce.
           */
          const { lastVisitedQueueId: _forgotten, ...rest } =
            state.workQueueViewPreferences;

          return {
            ...state,
            workQueueViewPreferences: nextQueueId
              ? { ...rest, lastVisitedQueueId: nextQueueId }
              : rest,
          };
        });
      },
      setWorkQueueFavouritesHintDismissed: (dismissed) => {
        set((state) =>
          state.workQueueViewPreferences.favouritesHintDismissed === dismissed
            ? state
            : {
                ...state,
                workQueueViewPreferences: {
                  ...state.workQueueViewPreferences,
                  favouritesHintDismissed: dismissed,
                },
              }
        );
      },
      setDashboardPickerFamily: (family) => {
        set((state) => {
          if (state.dashboardPickerPreferences.lastFamily === family) {
            return state;
          }

          /*
           * Forgetting the family OMITS the key rather than storing an
           * explicit `undefined`, so the persisted blob keeps the shape a
           * fresh browser would write. Same construction as the work-queue
           * marker above, for the same reason.
           */
          const { lastFamily: _forgotten, ...rest } =
            state.dashboardPickerPreferences;

          return {
            ...state,
            dashboardPickerPreferences:
              family === undefined ? rest : { ...rest, lastFamily: family },
          };
        });
      },
    }),
    {
      name: USER_PREFERENCES_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: ({
        themePreference,
        accentPalettePreference,
        displaySizePreference,
        riskAssessmentDetailViewPreferences,
        tableColumnVisibilityPreferences,
        tableColumnOrderPreferences,
        tableColumnWidthPreferences,
        tableColumnLegacyScopeClaims,
        workQueueViewPreferences,
        dashboardPickerPreferences,
      }) => ({
        themePreference,
        accentPalettePreference,
        displaySizePreference,
        riskAssessmentDetailViewPreferences,
        tableColumnVisibilityPreferences,
        tableColumnOrderPreferences,
        tableColumnWidthPreferences,
        tableColumnLegacyScopeClaims,
        workQueueViewPreferences,
        dashboardPickerPreferences,
      }),
    }
  )
);

export const useThemePreference = () =>
  useUserPreferencesStore((state) => state.themePreference);

export const useAccentPalettePreference = () =>
  useUserPreferencesStore((state) => state.accentPalettePreference);

export const useDisplaySizePreference = () =>
  useUserPreferencesStore((state) => state.displaySizePreference);

export const useRiskAssessmentDetailViewPreferences = () =>
  useUserPreferencesStore((state) => state.riskAssessmentDetailViewPreferences);

export const useTableColumnVisibilityPreference = (tableKey: string) =>
  useUserPreferencesStore(
    (state) => state.tableColumnVisibilityPreferences[tableKey]
  );

export const useTableColumnOrderPreference = (tableKey: string) =>
  useUserPreferencesStore(
    (state) => state.tableColumnOrderPreferences[tableKey]
  );

/**
 * Narrowed at the read, not at rehydration: `persist` restores this blob
 * without validating it, so a hand-edited or older `localStorage` value can
 * carry anything under this key. Returning a primitive keeps the selector
 * referentially stable, which a normalizing selector returning a fresh object
 * would not be.
 */
export const useLastVisitedWorkQueueId = () =>
  useUserPreferencesStore((state) => {
    const queueId = state.workQueueViewPreferences?.lastVisitedQueueId;

    return typeof queueId === 'string' && queueId ? queueId : undefined;
  });

export const useWorkQueueFavouritesHintDismissed = () =>
  useUserPreferencesStore(
    (state) => state.workQueueViewPreferences?.favouritesHintDismissed === true
  );

/**
 * The family the picker was last browsing, as a bare number.
 *
 * Narrowed at the read for the same reason as
 * {@link useLastVisitedWorkQueueId}, and only to "is a finite number". Whether
 * that number still names a family is a question only the dashboards feature
 * can answer, so the picker answers it and falls back to `All`.
 */
export const useDashboardPickerFamily = () =>
  useUserPreferencesStore((state) => {
    const family = state.dashboardPickerPreferences?.lastFamily;

    return typeof family === 'number' && Number.isFinite(family)
      ? family
      : undefined;
  });

export const useUserPreferencesActions = () =>
  useUserPreferencesStore((state) => ({
    setThemePreference: state.setThemePreference,
    setAccentPalettePreference: state.setAccentPalettePreference,
    setDisplaySizePreference: state.setDisplaySizePreference,
    setRiskAssessmentDetailViewPreferences:
      state.setRiskAssessmentDetailViewPreferences,
    setTableColumnVisibilityPreference:
      state.setTableColumnVisibilityPreference,
    setTableColumnOrderPreference: state.setTableColumnOrderPreference,
    setTableColumnWidthPreference: state.setTableColumnWidthPreference,
    claimTableColumnLegacyScope: state.claimTableColumnLegacyScope,
    setLastVisitedWorkQueueId: state.setLastVisitedWorkQueueId,
    setWorkQueueFavouritesHintDismissed:
      state.setWorkQueueFavouritesHintDismissed,
    setDashboardPickerFamily: state.setDashboardPickerFamily,
  }));

/**
 * Theme mode, accent palette, and display size live in one persisted blob, but
 * zustand does not automatically react when another tab updates that blob.
 * Keep the whole preference snapshot synchronized so external changes do not
 * snap the app back to stale colors, spacing, or density on the next local
 * write.
 *
 * `workQueueViewPreferences` is deliberately NOT synchronized here. It records
 * where this tab last was, and `setState` merges, so leaving it out keeps each
 * tab's own marker intact. Copying another tab's value across would mean opening
 * a queue in one tab silently changed where the other tab lands next.
 */
export function useSyncUserPreferencesStorageEvents() {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (
        event.key !== USER_PREFERENCES_STORAGE_KEY ||
        (event.storageArea && event.storageArea !== window.localStorage)
      ) {
        return;
      }

      const nextThemePreference = getThemePreferenceFromPersistedStorageValue(
        event.newValue
      );
      const nextAccentPalettePreference =
        getAccentPalettePreferenceFromPersistedStorageValue(event.newValue);
      const nextDisplaySizePreference =
        getDisplaySizePreferenceFromPersistedStorageValue(event.newValue);
      const nextTableColumnVisibilityPreferences =
        getTableColumnVisibilityPreferencesFromPersistedStorageValue(
          event.newValue
        );
      const nextTableColumnOrderPreferences =
        getTableColumnOrderPreferencesFromPersistedStorageValue(event.newValue);
      const nextTableColumnWidthPreferences =
        getTableColumnWidthPreferencesFromPersistedStorageValue(event.newValue);
      const nextTableColumnLegacyScopeClaims =
        getTableColumnLegacyScopeClaimsFromPersistedStorageValue(
          event.newValue
        );
      const nextRiskAssessmentDetailViewPreferences =
        getRiskAssessmentDetailViewPreferencesFromPersistedStorageValue(
          event.newValue
        );
      const currentState = useUserPreferencesStore.getState();

      if (
        currentState.themePreference === nextThemePreference &&
        currentState.accentPalettePreference === nextAccentPalettePreference &&
        currentState.displaySizePreference === nextDisplaySizePreference &&
        areRiskAssessmentDetailViewPreferencesEqual(
          currentState.riskAssessmentDetailViewPreferences,
          nextRiskAssessmentDetailViewPreferences
        ) &&
        areTableColumnVisibilityPreferencesEqual(
          currentState.tableColumnVisibilityPreferences,
          nextTableColumnVisibilityPreferences
        ) &&
        areTableColumnOrderPreferencesEqual(
          currentState.tableColumnOrderPreferences,
          nextTableColumnOrderPreferences
        ) &&
        areTableColumnWidthPreferencesEqual(
          currentState.tableColumnWidthPreferences,
          nextTableColumnWidthPreferences
        ) &&
        Object.keys(currentState.tableColumnLegacyScopeClaims ?? {}).length ===
          Object.keys(nextTableColumnLegacyScopeClaims).length &&
        Object.entries(nextTableColumnLegacyScopeClaims).every(
          ([key, scope]) =>
            currentState.tableColumnLegacyScopeClaims?.[key] === scope
        )
      ) {
        return;
      }

      useUserPreferencesStore.setState({
        themePreference: nextThemePreference,
        accentPalettePreference: nextAccentPalettePreference,
        displaySizePreference: nextDisplaySizePreference,
        riskAssessmentDetailViewPreferences:
          nextRiskAssessmentDetailViewPreferences,
        tableColumnVisibilityPreferences: nextTableColumnVisibilityPreferences,
        tableColumnOrderPreferences: nextTableColumnOrderPreferences,
        tableColumnWidthPreferences: nextTableColumnWidthPreferences,
        tableColumnLegacyScopeClaims: nextTableColumnLegacyScopeClaims,
      });
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);
}

/**
 * Persist middleware hydrates after the initial client render. Exposing that
 * lifecycle lets theme consumers avoid momentarily snapping back to the default
 * "system" mode before local storage finishes restoring the user's last choice.
 */
export function useUserPreferencesHydrated() {
  const persistApi = useUserPreferencesStore.persist;
  const hasHydrated = useSyncExternalStore(
    (onStoreChange) => {
      /**
       * The persisted zustand API is available in normal browser execution, but
       * some render/runtime wrappers can evaluate the hook before that metadata
       * is attached. Treat those cases as already hydrated so the theme
       * controller never crashes the route render path.
       */
      if (!persistApi?.onHydrate || !persistApi.onFinishHydration) {
        return () => {};
      }

      const unsubscribeHydrate = persistApi.onHydrate(onStoreChange);
      const unsubscribeFinishHydration =
        persistApi.onFinishHydration(onStoreChange);

      return () => {
        unsubscribeHydrate();
        unsubscribeFinishHydration();
      };
    },
    () => persistApi?.hasHydrated?.() ?? true,
    /**
     * Server rendering must report "not hydrated" so SSR-only consumers can
     * keep using the request-scoped cookie snapshot. Returning `true` here
     * causes providers like the MUI theme bootstrap to ignore their explicit
     * initial props and fall back to the zustand store's default values, which
     * shows the wrong accent or theme for the first painted frame until the
     * browser store finishes rehydrating.
     */
    () => false
  );

  return hasHydrated;
}
